import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageTitleStore, type PageTitleStatus } from './pageTitleStore';

/**
 * Publish a location-scoped title override; clears on unmount / key change.
 * Only surfaces that already know loading/error/label should call this.
 * Pass `enabled: false` on section roots so route rules keep the title.
 */
export function useRoutePageTitle(opts: {
  isLoading?: boolean;
  isError?: boolean;
  label?: string;
  enabled?: boolean;
}): void {
  const { key } = useLocation();
  const setPageTitleState = usePageTitleStore((s) => s.setPageTitleState);
  const { isLoading, isError, label, enabled = true } = opts;

  useEffect(() => {
    if (!enabled) {
      setPageTitleState(key, null);
      return;
    }

    let status: PageTitleStatus = 'ready';
    if (isLoading) status = 'loading';
    else if (isError) status = 'error';

    setPageTitleState(key, { status, label });
    return () => {
      setPageTitleState(key, null);
    };
  }, [key, isLoading, isError, label, enabled, setPageTitleState]);
}
